// Recurrence Engine for Auto-Scheduling Count Sessions
// Generates count sessions based on RRULE-style recurrence patterns

export class RecurrenceEngine {
  constructor() {
    this.weekDays = {
      'sunday': 0,
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6
    };
  }

  // Generate count sessions for a schedule within a date range
  generateSessions(schedule, startDate, endDate, maxSessions = 100) {
    const sessions = [];
    const pattern = schedule.recurrence_pattern;
    
    if (!pattern || !pattern.frequency) {
      console.warn('Invalid recurrence pattern:', pattern);
      return sessions;
    }

    let currentDate = new Date(Math.max(new Date(schedule.start_date), startDate));
    const finalEndDate = new Date(Math.min(
      schedule.end_date ? new Date(schedule.end_date) : endDate,
      endDate
    ));

    let occurrenceCount = 0;
    const maxOccurrences = maxSessions;

    while (currentDate <= finalEndDate && occurrenceCount < maxOccurrences) {
      // Check if this date should be skipped (blackout dates)
      const dateString = currentDate.toISOString().split('T')[0];
      if (schedule.blackout_dates && schedule.blackout_dates.includes(dateString)) {
        currentDate = this.getNextDate(currentDate, pattern);
        continue;
      }

      // Generate session for this date
      const session = this.createSessionFromSchedule(schedule, currentDate);
      sessions.push(session);
      
      occurrenceCount++;
      currentDate = this.getNextDate(currentDate, pattern);
    }

    return sessions;
  }

  // Get the next occurrence date based on recurrence pattern
  getNextDate(currentDate, pattern) {
    const nextDate = new Date(currentDate);
    
    switch (pattern.frequency) {
      case 'daily':
        nextDate.setDate(nextDate.getDate() + (pattern.interval || 1));
        break;
        
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + (7 * (pattern.interval || 1)));
        break;
        
      case 'monthly':
        if (pattern.day_of_month !== undefined) {
          // Monthly by date (e.g., 15th of each month)
          nextDate.setMonth(nextDate.getMonth() + (pattern.interval || 1));
          if (pattern.day_of_month === -1) {
            // Last day of month
            nextDate.setMonth(nextDate.getMonth() + 1, 0);
          } else {
            nextDate.setDate(pattern.day_of_month);
          }
        } else if (pattern.week_of_month !== undefined && pattern.day_of_week) {
          // Monthly by weekday (e.g., 3rd Thursday)
          nextDate.setMonth(nextDate.getMonth() + (pattern.interval || 1));
          this.setToNthWeekdayOfMonth(nextDate, pattern.week_of_month, pattern.day_of_week);
        }
        break;
        
      default:
        console.warn('Unknown frequency:', pattern.frequency);
        nextDate.setDate(nextDate.getDate() + 1);
    }
    
    return nextDate;
  }

  // Set date to the nth occurrence of a weekday in the month
  setToNthWeekdayOfMonth(date, weekNumber, dayOfWeek) {
    const targetDayOfWeek = this.weekDays[dayOfWeek.toLowerCase()];
    
    if (weekNumber === -1) {
      // Last occurrence of the weekday in the month
      date.setMonth(date.getMonth() + 1, 0); // Last day of month
      while (date.getDay() !== targetDayOfWeek) {
        date.setDate(date.getDate() - 1);
      }
    } else {
      // Nth occurrence of the weekday
      date.setDate(1); // First day of month
      
      // Find first occurrence of the target weekday
      while (date.getDay() !== targetDayOfWeek) {
        date.setDate(date.getDate() + 1);
      }
      
      // Add weeks to get to the nth occurrence
      date.setDate(date.getDate() + (7 * (weekNumber - 1)));
    }
  }

  // Create a count session object from a schedule and date
  createSessionFromSchedule(schedule, sessionDate) {
    const visibleFromDate = this.calculateVisibleFromDate(sessionDate, schedule.show_days_before || 0);
    
    return {
      store_id: schedule.store_id,
      template_id: schedule.template_id,
      schedule_id: schedule.id,
      name: this.generateSessionName(schedule, sessionDate),
      session_date: sessionDate.toISOString().split('T')[0],
      visible_from_date: visibleFromDate,
      created_time: schedule.schedule_time || '3:00 PM',
      count_commence_time: schedule.schedule_time || '3:00 PM',
      user_timezone: schedule.user_timezone || 'America/Los_Angeles',
      status: 'scheduled',
      created_by: schedule.created_by,
      notes: `Auto-generated from schedule: ${schedule.name}`,
      is_auto_generated: true,
      was_modified: false
    };
  }

  // Generate a session name based on the schedule and date
  generateSessionName(schedule, sessionDate) {
    const dateStr = sessionDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
    
    return `🔄 ${dateStr} - ${schedule.name} (Auto)`;
  }

  // Calculate when a session should become visible in the count interface
  calculateVisibleFromDate(sessionDate, showDaysBefore) {
    const visibleDate = new Date(sessionDate);
    visibleDate.setDate(visibleDate.getDate() - showDaysBefore);
    return visibleDate.toISOString().split('T')[0];
  }

  // Check if a session should be visible in the count interface today
  isSessionVisible(sessionDate, showDaysBefore) {
    const today = new Date().toISOString().split('T')[0];
    const visibleFromDate = this.calculateVisibleFromDate(new Date(sessionDate), showDaysBefore);
    return today >= visibleFromDate;
  }

  // Validate a recurrence pattern
  validatePattern(pattern) {
    if (!pattern || !pattern.frequency) {
      return { valid: false, error: 'Frequency is required' };
    }

    switch (pattern.frequency) {
      case 'daily':
        if (!pattern.interval || pattern.interval < 1) {
          return { valid: false, error: 'Daily interval must be at least 1' };
        }
        break;
        
      case 'weekly':
        if (!pattern.interval || pattern.interval < 1) {
          return { valid: false, error: 'Weekly interval must be at least 1' };
        }
        if (!pattern.days || pattern.days.length === 0) {
          return { valid: false, error: 'At least one day must be selected for weekly recurrence' };
        }
        break;
        
      case 'monthly':
        if (!pattern.interval || pattern.interval < 1) {
          return { valid: false, error: 'Monthly interval must be at least 1' };
        }
        if (pattern.day_of_month === undefined && 
            (pattern.week_of_month === undefined || !pattern.day_of_week)) {
          return { valid: false, error: 'Monthly pattern must specify either day of month or week/day combination' };
        }
        break;
        
      default:
        return { valid: false, error: `Unknown frequency: ${pattern.frequency}` };
    }

    return { valid: true };
  }

  // Generate a human-readable description of the recurrence pattern
  describePattern(pattern) {
    if (!pattern || !pattern.frequency) {
      return 'Invalid pattern';
    }

    const interval = pattern.interval || 1;
    const intervalText = interval === 1 ? '' : `every ${interval} `;

    switch (pattern.frequency) {
      case 'daily':
        return interval === 1 ? 'Daily' : `Every ${interval} days`;
        
      case 'weekly':
        const days = pattern.days || [];
        const dayNames = days.map(day => 
          day.charAt(0).toUpperCase() + day.slice(1, 3)
        ).join(', ');
        
        if (interval === 1) {
          return `Weekly on ${dayNames}`;
        } else {
          return `Every ${interval} weeks on ${dayNames}`;
        }
        
      case 'monthly':
        let monthlyDesc = interval === 1 ? 'Monthly' : `Every ${interval} months`;
        
        if (pattern.day_of_month !== undefined) {
          if (pattern.day_of_month === -1) {
            monthlyDesc += ' on the last day';
          } else {
            monthlyDesc += ` on the ${this.ordinal(pattern.day_of_month)}`;
          }
        } else if (pattern.week_of_month !== undefined && pattern.day_of_week) {
          const weekDesc = pattern.week_of_month === -1 ? 'last' : this.ordinal(pattern.week_of_month);
          const dayName = pattern.day_of_week.charAt(0).toUpperCase() + pattern.day_of_week.slice(1);
          monthlyDesc += ` on the ${weekDesc} ${dayName}`;
        }
        
        return monthlyDesc;
        
      default:
        return 'Custom pattern';
    }
  }

  // Convert number to ordinal (1st, 2nd, 3rd, etc.)
  ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  // Check if a date falls within restaurant 4-4-5 accounting periods
  isRestaurantPeriodBoundary(date, useRestaurantPeriods) {
    if (!useRestaurantPeriods) return false;
    
    // Restaurant 4-4-5 calendar logic would go here
    // This is a simplified version - full implementation would need
    // to account for the complex 4-4-5 calendar rules
    
    const dayOfMonth = date.getDate();
    const month = date.getMonth();
    
    // Simplified: assume period boundaries are roughly every 4-5 weeks
    // Real implementation would use proper 4-4-5 calendar calculations
    return (dayOfMonth === 1 && [0, 3, 6, 9].includes(month)) ||
           (dayOfMonth === 29 && [1, 4, 7, 10].includes(month));
  }
}

// Schedule Processor - Handles generating sessions from active schedules
export class ScheduleProcessor {
  constructor(supabaseClient) {
    this.supabase = supabaseClient;
    this.engine = new RecurrenceEngine();
  }

  // Process all active schedules and generate sessions
  async processSchedules(daysAhead = 30) {
    try {
      console.log(`Processing schedules for ${daysAhead} days ahead...`);
      
      // Get all active schedules
      const { data: schedules, error: schedulesError } = await this.supabase
        .from('count_schedules')
        .select('*')
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString().split('T')[0]);

      if (schedulesError) {
        console.error('Error loading schedules:', schedulesError);
        return { success: false, error: schedulesError.message };
      }

      if (!schedules || schedules.length === 0) {
        console.log('No active schedules found');
        return { success: true, processed: 0, created: 0 };
      }

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + daysAhead);

      let totalCreated = 0;
      let totalProcessed = 0;

      for (const schedule of schedules) {
        try {
          const result = await this.processSchedule(schedule, startDate, endDate);
          totalCreated += result.created;
          totalProcessed++;
          
          // Update the schedule's next generation date
          await this.updateScheduleNextGeneration(schedule.id, endDate);
          
        } catch (error) {
          console.error(`Error processing schedule ${schedule.id}:`, error);
          // Continue with other schedules
        }
      }

      console.log(`Processed ${totalProcessed} schedules, created ${totalCreated} sessions`);
      return { 
        success: true, 
        processed: totalProcessed, 
        created: totalCreated 
      };

    } catch (error) {
      console.error('Error in processSchedules:', error);
      return { success: false, error: error.message };
    }
  }

  // Process a single schedule
  async processSchedule(schedule, startDate, endDate) {
    console.log(`Processing schedule: ${schedule.name}`);
    
    // Generate sessions for this schedule
    const sessions = this.engine.generateSessions(schedule, startDate, endDate);
    
    if (sessions.length === 0) {
      console.log(`No sessions generated for schedule: ${schedule.name}`);
      return { created: 0 };
    }

    let createdCount = 0;

    for (const sessionData of sessions) {
      try {
        // Check if session already exists for this date and schedule
        const existingSession = await this.findExistingSession(
          schedule.id, 
          sessionData.session_date
        );

        if (existingSession) {
          console.log(`Session already exists for ${sessionData.session_date}, skipping`);
          continue;
        }

        // Create the count session
        const { data: session, error: sessionError } = await this.supabase
          .from('count_sessions')
          .insert({
            store_id: sessionData.store_id,
            template_id: sessionData.template_id,
            name: sessionData.name,
            session_date: sessionData.session_date,
            created_time: sessionData.created_time,
            count_commence_time: sessionData.count_commence_time,
            user_timezone: sessionData.user_timezone,
            status: sessionData.status,
            created_by: sessionData.created_by,
            notes: sessionData.notes
          })
          .select()
          .single();

        if (sessionError) {
          console.error('Error creating session:', sessionError);
          continue;
        }

        // Create the scheduled session link
        const { error: linkError } = await this.supabase
          .from('scheduled_count_sessions')
          .insert({
            session_id: session.id,
            schedule_id: schedule.id,
            visible_from_date: sessionData.scheduled_count_sessions[0].visible_from_date,
            is_auto_generated: true,
            was_modified: false
          });

        if (linkError) {
          console.error('Error creating scheduled session link:', linkError);
          // Clean up the session if link creation failed
          await this.supabase
            .from('count_sessions')
            .delete()
            .eq('id', session.id);
          continue;
        }

        console.log(`Created session: ${sessionData.name} for ${sessionData.session_date}`);
        createdCount++;

      } catch (error) {
        console.error('Error creating session:', error);
      }
    }

    return { created: createdCount };
  }

  // Find existing session for a schedule and date
  async findExistingSession(scheduleId, sessionDate) {
    const { data, error } = await this.supabase
      .from('scheduled_count_sessions')
      .select(`
        *,
        count_sessions (
          session_date
        )
      `)
      .eq('schedule_id', scheduleId)
      .eq('count_sessions.session_date', sessionDate)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error checking for existing session:', error);
    }

    return data;
  }

  // Update schedule's next generation date
  async updateScheduleNextGeneration(scheduleId, nextDate) {
    const { error } = await this.supabase
      .from('count_schedules')
      .update({ 
        next_generation_date: nextDate.toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      })
      .eq('id', scheduleId);

    if (error) {
      console.error('Error updating schedule next generation date:', error);
    }
  }
}

// Export singleton instance
export const recurrenceEngine = new RecurrenceEngine();
